<template>
    <div
        :id="id"
        ref="container"
        :data-testid="testId || 'inputautocomplete-wrapper'"
        class="tl-input-wrapper"
    >
        <FloatLabel
            :id="inputId"
            :label="label"
        >
            <input
                :id="inputId"
                ref="input"
                :value="model"
                type="text"
                role="combobox"
                aria-autocomplete="list"
                aria-haspopup="listbox"
                :aria-expanded="dropdownOpen"
                :aria-owns="`${inputId}-listbox`"
                :aria-controls="`${inputId}-listbox`"
                :aria-activedescendant="highlightedOptionIndex !== -1 ? `${inputId}-option-${highlightedOptionIndex}` : undefined"
                :disabled="disabled"
                :placeholder="placeholder"
                :class="{
                    'tl-input': true,
                    'tl-input-filled': !!model,
                    'tl-input-disabled': disabled,
                    'tl-input-focus': focused,
                    'tl-input-invalid': !!error,
                }"
                :aria-invalid="!!error || undefined"
                @input="handleInput"
                @focus="handleFocus"
                @blur="handleBlur"
                @keydown="handleKeyDown"
            />

            <span
                v-if="optionsLoading"
                class="tl-input-autocomplete-icon"
            >
                <LoadingCircle />
            </span>
        </FloatLabel>

        <Popover
            ref="popoverComponentRef"
            v-model="dropdownOpen"
            :reference="container"
            :offset="3"
            match-reference-width
        >
            <div
                :id="`${inputId}-listbox`"
                class="tl-input-autocomplete-overlay"
                :aria-hidden="!dropdownOpen"
            >
                <ul
                    v-if="items.length"
                    ref="listbox"
                    class="tl-input-autocomplete-list"
                    role="listbox"
                >
                    <li
                        v-for="(item, index) in items"
                        :id="`${inputId}-option-${index}`"
                        :key="index"
                        class="tl-input-autocomplete-option"
                        :class="{ 'tl-input-option-highlighted': highlightedOptionIndex === index }"
                        role="option"
                        :aria-selected="highlightedOptionIndex === index"
                        data-testid="input-autocomplete-options-item"
                        @mousedown.prevent="handleSelect(item)"
                        @mousemove="highlightedOptionIndex = index"
                    >
                        <Words
                            oneline
                            class="text-break"
                        >{{ itemLabel(item) }}</Words>
                    </li>
                </ul>

                <Words
                    v-else-if="noResultsVisible"
                    class="tl-input-autocomplete-no-results"
                    data-testid="input-autocomplete-no-results"
                >{{ t('input-autocomplete.no-results') }}</Words>
            </div>
        </Popover>

        <ErrorText
            v-if="error"
            :error="error"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useId, watch } from 'vue'
import { clamp } from 'ramda'
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import Words from 'widget-components/components/Words/Words.vue'
import FloatLabel from '../FloatLabel/FloatLabel.vue'
import LoadingCircle from '../LoadingCircle/LoadingCircle.vue'
import Popover from 'widget-components/atoms/Popover/Popover.vue'
import { autoFocusOnMounted } from 'widget-components/integrations/CustomPage/autofocus'
import { useI18n } from 'widget-components/utilities/i18n'
import type { InputAutocompleteItem, InputAutocompleteProps } from './types'

defineOptions({ inheritAttrs: false })

const { t } = useI18n()

const props = withDefaults(defineProps<InputAutocompleteProps>(), { noResultsEligible: true })

const emit = defineEmits<{
    search: [query: string]
    select: [item: InputAutocompleteItem]
}>()

const model = defineModel<string | null>()

const container = ref<HTMLDivElement>()
const input = ref<HTMLInputElement>()
const listbox = ref<HTMLUListElement>()
const popoverComponentRef = ref<InstanceType<typeof Popover>>()

const dropdownOpen = ref(false)
const highlightedOptionIndex = ref(-1)
const focused = ref(false)

const inputId = computed(() => props.id ? `${props.id}-input` : useId())
const items = computed(() => props.items ?? [])
const noResultsVisible = computed(() => !props.optionsLoading && !!model.value && props.noResultsEligible)

autoFocusOnMounted(props, () => input.value)

const openDropdown = () => {
    if (props.disabled) {
        return
    }

    dropdownOpen.value = true
}

const closeDropdown = () => {
    dropdownOpen.value = false
    highlightedOptionIndex.value = -1
}

const handleInput = (event: Event) => {
    const value = (event.target as HTMLInputElement).value

    model.value = value
    highlightedOptionIndex.value = -1
    openDropdown()
    emit('search', value)
}

const handleFocus = () => {
    focused.value = true

    if (items.value.length) {
        openDropdown()
    }
}

const handleBlur = () => {
    focused.value = false
    closeDropdown()
}

const handleSelect = (item: InputAutocompleteItem) => {
    model.value = props.itemLabel(item)
    closeDropdown()
    input.value?.blur()
    emit('select', item)
}

const updateHighlightedOption = (index: number) => {
    highlightedOptionIndex.value = index

    nextTick(() => {
        listbox.value?.children[index]?.scrollIntoView({ block: 'nearest' })
    })
}

const handleKeyDown = (event: KeyboardEvent) => {
    if (props.disabled) {
        return
    }

    const lastIndex = Math.max(items.value.length - 1, 0)

    switch (event.code) {
        case 'ArrowDown':
            event.preventDefault()
            if (!dropdownOpen.value) {
                openDropdown()
            } else {
                updateHighlightedOption(clamp(0, lastIndex, highlightedOptionIndex.value + 1))
            }
            break

        case 'ArrowUp':
            event.preventDefault()
            if (dropdownOpen.value) {
                updateHighlightedOption(clamp(0, lastIndex, highlightedOptionIndex.value - 1))
            }
            break

        case 'Enter':
            if (dropdownOpen.value && highlightedOptionIndex.value !== -1) {
                event.preventDefault()
                const item = items.value[highlightedOptionIndex.value]
                if (item) {
                    handleSelect(item)
                }
            }
            break

        case 'Escape':
            closeDropdown()
            break

        default:
            break
    }
}

watch(() => items.value.length, () => {
    popoverComponentRef.value?.update()
})
</script>

<style lang="sass" scoped>
@import './InputAutocomplete.sass'
</style>
